/**
 * Garment color rendering engine (HTML5 Canvas).
 *
 * Single source of truth for tinting a neutral/grayscale garment image to a solid
 * HEX color while preserving fabric folds, creases, highlights and shadows. Used by:
 *  - live designer preview (GarmentTintCanvas.vue)
 *  - admin catalog preview (ProductEditView.vue, via GarmentTintLayer.vue)
 *  - export / cart / catalog-thumb (useCanvasRenderer.ts -> drawTintedGarmentLayer)
 *
 * The blend keeps shading because `multiply` darkens the solid color by the
 * grayscale luminance of the garment (white -> color, black -> black, mid -> shaded),
 * and the result is masked back to the garment alpha so only the shirt shape is painted.
 */

import type { GarmentTintMode } from './garmentTint';
import { cssHexColor, recolorImageDataRegion } from './garmentTint';
import { loadImageBitmap } from './imageLoader';

export interface RenderTintOptions {
  /** Neutral/grayscale garment image (already loaded). */
  baseImg: HTMLImageElement;
  /** Target color (may include leading '#' or multi-hex 'aa;bb' — first segment is used). */
  hex: string;
  /** Blend strategy. */
  mode: GarmentTintMode;
  /** Optional repeating texture (already loaded); tiled over the shirt only. */
  textureImg?: HTMLImageElement | null;
  /** Texture strength; lower keeps more of the underlying folds. */
  texturePatternAlpha?: number;
  /** CSS (layout) size of the target canvas in pixels. */
  cssW: number;
  cssH: number;
  /** Device pixel ratio for retina-sharp output (defaults to window.devicePixelRatio). */
  dpr?: number;
}

interface ContainRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** object-contain math: fit `img` inside `boxW x boxH`, centered, preserving aspect ratio. */
function containRect(
  imgW: number,
  imgH: number,
  boxW: number,
  boxH: number,
): ContainRect {
  if (imgW <= 0 || imgH <= 0 || boxW <= 0 || boxH <= 0) {
    return { x: 0, y: 0, w: boxW, h: boxH };
  }
  const scale = Math.min(boxW / imgW, boxH / imgH);
  const w = imgW * scale;
  const h = imgH * scale;
  return { x: (boxW - w) / 2, y: (boxH - h) / 2, w, h };
}

/**
 * Build an offscreen canvas holding the tinted (and optionally textured) garment,
 * sized to `rect.w x rect.h` device pixels. Returns null if a 2D context is unavailable.
 */
export function buildTintedGarmentCanvas(
  baseImg: HTMLImageElement,
  rectW: number,
  rectH: number,
  hex: string,
  mode: GarmentTintMode,
  textureImg?: HTMLImageElement | null,
  texturePatternAlpha = 0.5,
): HTMLCanvasElement | null {
  const w = Math.max(1, Math.ceil(rectW));
  const h = Math.max(1, Math.ceil(rectH));
  const temp = document.createElement('canvas');
  temp.width = w;
  temp.height = h;
  const tctx = temp.getContext('2d');
  if (!tctx) return null;

  // 1) Draw the neutral garment as the base layer (provides shape + alpha + shading).
  tctx.drawImage(baseImg, 0, 0, w, h);

  if (mode === 'none') {
    // No tint: base only.
  } else if (mode === 'recolor') {
    // Flat recolor: every opaque pixel becomes the solid color (alpha preserved).
    recolorImageDataRegion(tctx, 0, 0, w, h, hex);
  } else {
    // 2) Multiply a solid color over the grayscale garment: shading is retained
    //    because multiply scales the color channel by the garment luminance.
    tctx.globalCompositeOperation = 'multiply';
    tctx.fillStyle = cssHexColor(hex);
    tctx.fillRect(0, 0, w, h);
    // 3) Re-mask to the garment alpha so the rectangle of color is clipped to the
    //    shirt silhouette (this is what the broken CSS preview was missing).
    tctx.globalCompositeOperation = 'destination-in';
    tctx.drawImage(baseImg, 0, 0, w, h);
    tctx.globalCompositeOperation = 'source-over';
  }

  // 4) Texture via Pattern Filling + Source Composition. `source-atop` paints the
  //    repeating pattern only where shirt pixels already exist, and the partial
  //    alpha lets the multiply shading (folds) show through.
  if (textureImg && mode !== 'none') {
    const pattern = tctx.createPattern(textureImg, 'repeat');
    if (pattern) {
      tctx.save();
      tctx.globalCompositeOperation = 'source-atop';
      tctx.globalAlpha = Math.max(0, Math.min(1, texturePatternAlpha));
      tctx.fillStyle = pattern;
      tctx.fillRect(0, 0, w, h);
      tctx.restore();
    }
  }

  return temp;
}

/**
 * Render a tinted garment into `canvas` at retina resolution.
 *
 * @returns false when the 2D context is unavailable (caller should fall back to <img>).
 */
export function renderTintedGarment(
  canvas: HTMLCanvasElement,
  o: RenderTintOptions,
): boolean {
  const ctx = canvas.getContext('2d');
  if (!ctx) return false; // error boundary: no 2D support

  const cssW = Math.max(1, o.cssW);
  const cssH = Math.max(1, o.cssH);
  const dpr = Math.max(1, o.dpr ?? (typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1));

  // Backing store in device pixels for sharp retina output; CSS size stays logical.
  const bw = Math.round(cssW * dpr);
  const bh = Math.round(cssH * dpr);
  if (canvas.width !== bw) canvas.width = bw;
  if (canvas.height !== bh) canvas.height = bh;
  canvas.style.width = `${cssW}px`;
  canvas.style.height = `${cssH}px`;

  // Draw in CSS pixel space; clear fully each frame (no leftover pixels between redraws).
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, cssW, cssH);

  const natW = o.baseImg.naturalWidth || o.baseImg.width;
  const natH = o.baseImg.naturalHeight || o.baseImg.height;
  const rect = containRect(natW, natH, cssW, cssH);

  if (o.mode === 'none') {
    ctx.drawImage(o.baseImg, rect.x, rect.y, rect.w, rect.h);
    return true;
  }

  const tinted = buildTintedGarmentCanvas(
    o.baseImg,
    rect.w * dpr,
    rect.h * dpr,
    o.hex,
    o.mode,
    o.textureImg,
    o.texturePatternAlpha,
  );
  if (!tinted) {
    // Fallback: at least show the untinted garment rather than nothing.
    ctx.drawImage(o.baseImg, rect.x, rect.y, rect.w, rect.h);
    return true;
  }
  ctx.drawImage(tinted, rect.x, rect.y, rect.w, rect.h);
  return true;
}

/**
 * Draw garment layer with optional multiply or recolor tint + texture (export/cart/thumb).
 *
 * Uses the same buildTintedGarmentCanvas as the live designer so exported PNGs match
 * pixel-for-pixel (masked texture pattern, no overlay bleed).
 */
export async function drawTintedGarmentLayer(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  left: number,
  top: number,
  lw: number,
  lh: number,
  hex: string,
  textureUrl: string | undefined,
  tintMode: GarmentTintMode,
  loadImage: (url: string) => Promise<HTMLImageElement>,
): Promise<void> {
  if (tintMode === 'none') {
    ctx.drawImage(img, left, top, lw, lh);
    return;
  }

  let textureImg: HTMLImageElement | null = null;
  if (textureUrl) {
    textureImg = await loadImage(textureUrl).catch(() => null);
  }

  const temp = buildTintedGarmentCanvas(img, lw, lh, hex, tintMode, textureImg);
  if (!temp) {
    ctx.drawImage(img, left, top, lw, lh);
    return;
  }
  ctx.drawImage(temp, left, top, lw, lh);
}

/** Load (and cache) an image through the shared loader, so garments and canvas renders share decodes. */
export function loadGarmentImage(src: string): Promise<HTMLImageElement> {
  return loadImageBitmap(src);
}
